import { z } from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithLog } from "src/api/apiErrorLogs";

const DiagnosticFactSchema = z.object({
    value: z.string(),
    formattedValue: z.string(),
    label: z.string(),
    unit: z.string().nullable(),
    improvement: z.enum(["decrease", "increase"]).nullable(),
});

const DiagnosticFactOutputSchema = z.object({
    facts: z.record(z.string(), DiagnosticFactSchema),
    catalog: z.record(z.string(), z.string()).nullish(),
});

export type DiagnosticFact = z.infer<typeof DiagnosticFactSchema>;

export type DiagnosticFactsParams = {
    campaignId?: number;
    ssqVersionId?: number;
    entityType?: string;
    entityId?: number;
    organizationId?: number;
    enabled?: boolean;
    catalog?: boolean;
};

export const useDiagnosticFacts = (params: DiagnosticFactsParams) => {
    const { campaignId, ssqVersionId, entityType, entityId, organizationId, enabled = true, catalog = false } = params;
    const { i18n } = useTranslation();

    const isReady =
        enabled &&
        typeof campaignId === "number" &&
        campaignId > 0 &&
        typeof ssqVersionId === "number" &&
        ssqVersionId > 0 &&
        typeof entityType === "string" &&
        entityType !== "" &&
        typeof entityId === "number" &&
        entityId > 0 &&
        typeof organizationId === "number" &&
        organizationId > 0;

    const query = useQuery({
        queryKey: [
            "diagnosticFacts",
            campaignId,
            ssqVersionId,
            entityType,
            entityId,
            organizationId,
            catalog,
            i18n.resolvedLanguage,
        ],
        enabled: isReady,
        staleTime: 2 * 60 * 1000,
        queryFn: async () => {
            const search = new URLSearchParams({
                campaignId: String(campaignId),
                ssqVersionId: String(ssqVersionId),
                entityType: entityType ?? "",
                entityId: String(entityId),
            });
            if (catalog) {
                search.set("catalog", "true");
            }
            return loggedInApiCall.get(`/api/diagnostic-facts?${search.toString()}`, {
                params: {
                    organization_id: organizationId,
                },
                headers: {
                    Accept: "application/json",
                },
            });
        },
        select: (response) => {
            const parsed = zodParseWithLog(response.data, DiagnosticFactOutputSchema, "diagnosticFacts");
            return { facts: parsed.facts, catalog: parsed.catalog ?? undefined };
        },
    });

    return {
        facts: query.data?.facts,
        catalog: query.data?.catalog,
        ...query,
    };
};
